import { complexCommands, simpleCommands } from "src/data/commands";
import i18n from "src/i18n/config";

import { MdmPolicyCommand } from "@devices/types";

const knownCommandNames = new Set<string>([
    ...complexCommands.map((command) => command.name),
    ...simpleCommands.map((command) => command.template?.command).filter((name): name is string => Boolean(name)),
]);

const knownCommandDisplayNames = new Map<string, string>();
complexCommands.forEach((command) => {
    knownCommandDisplayNames.set(command.name, command.schema.title);
});
simpleCommands.forEach((command) => {
    const commandName = command.template?.command;
    const displayName = command.template?.caption ?? command.schema.title;
    if (commandName && displayName && !knownCommandDisplayNames.has(commandName)) {
        knownCommandDisplayNames.set(commandName, displayName);
    }
});

export type PolicyCommandLabels = {
    primary: string;
    secondary: string | null;
    recognized: boolean;
};

const getCommandName = (policyCommand: MdmPolicyCommand): string | null => {
    const command = policyCommand.command;
    return typeof command === "string" && command.trim() ? command : null;
};

const getCaption = (policyCommand: MdmPolicyCommand): string | null => {
    const caption = policyCommand.caption;
    return typeof caption === "string" && caption.trim() ? caption : null;
};

export const isPolicyCommandRecognized = (policyCommand: MdmPolicyCommand): boolean => {
    const commandName = getCommandName(policyCommand);
    return commandName ? knownCommandNames.has(commandName) : false;
};

export const getPolicyCommandLabels = (policyCommand: MdmPolicyCommand): PolicyCommandLabels => {
    const commandName = getCommandName(policyCommand);
    const caption = getCaption(policyCommand);

    if (!commandName) {
        return {
            primary: i18n.t("modals:importGroupPolicy.preview.invalidEntry"),
            secondary: null,
            recognized: false,
        };
    }

    const recognized = knownCommandNames.has(commandName);

    if (!recognized) {
        return {
            primary: i18n.t("modals:importGroupPolicy.preview.unrecognizedCommandTitle"),
            secondary: i18n.t("modals:importGroupPolicy.preview.commandName", { name: commandName }),
            recognized: false,
        };
    }

    const fallbackName = knownCommandDisplayNames.get(commandName) ?? commandName;
    const primary = caption ?? fallbackName;

    return {
        primary,
        secondary: caption ? i18n.t("modals:importGroupPolicy.preview.commandName", { name: commandName }) : null,
        recognized: true,
    };
};
